import type { ReactElement } from 'react';
import { useCallback, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import type {
  TpCountriesForSelect,
  TpCountryForSelect,
} from '@noah-labs/fe-shared-data-access-countries';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { TpSelectOption } from '../mui/forms/SearchSelect';
import { PrimaryButton } from '../mui/buttons/PrimaryButton';
import { DialogSelectField } from '../mui/forms/DialogSelectField';

type PpChooseCountryForm = {
  countries: TpCountriesForSelect['countries'] | undefined;
  defaultCountry?: TpCountryForSelect | undefined;
  focused?: boolean | undefined;
  onNext: (CountryCode: string) => void;
};

const countrySchema = z.object({
  CountryCode: z.string().trim().min(1, 'Country is required'),
  CountryName: z.string().trim().min(1, 'Country is required'),
});

type TpCountrySchema = z.infer<typeof countrySchema>;

export function ChooseCountryForm({
  countries,
  defaultCountry,
  focused = false,
  onNext,
}: PpChooseCountryForm): ReactElement {
  const methods = useForm<TpCountrySchema>({
    defaultValues: {
      CountryCode: '',
      CountryName: '',
    },
    mode: 'onBlur',
    resolver: zodResolver(countrySchema),
  });

  useEffect(() => {
    if (!defaultCountry) {
      return;
    }
    methods.reset({
      CountryCode: defaultCountry.value,
      CountryName: defaultCountry.label,
    });
  }, [defaultCountry, methods]);

  const handleCountryFormValues = useCallback(
    (value: TpSelectOption | null) => {
      methods.setValue('CountryCode', value?.value || '', {
        shouldValidate: true,
      });
      methods.setValue('CountryName', value?.label || '', {
        shouldValidate: true,
      });
    },

    [methods],
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit((value) => onNext(value.CountryCode))}>
        <DialogSelectField
          fullWidth
          required
          focused={focused}
          helperText=" "
          isFetched={!!countries}
          label="Country"
          name="CountryName"
          options={countries}
          onChange={handleCountryFormValues}
        />
        <PrimaryButton disabled={!methods.formState.isValid} size="medium" type="submit">
          Continue
        </PrimaryButton>
      </form>
    </FormProvider>
  );
}
